import {
    FC,
    FormEvent,
    ReactElement,
    useEffect,
    useRef,
    useState,
} from 'react';

import { useToggle } from 'react-use';

import {
    FormActionButtons,
    IconButton,
    SearchableValueInput,
    SkillList,
    SkillsSelector,
} from '../../../compositions';
import { sortSkillOptions } from '../../../helpers/skills';
import trans from '../../../helpers/trans';
import { SkillBase, SkillsFormData } from '../../../models/@competentNL/Skills/Skills';
import { DialogFormProps, FormOption, SearchableOption } from '../../../types';

import './SkillsForm.scss';

interface SkillsFormProps extends DialogFormProps<SkillsFormData> {
    showSearch?: boolean;
    showOccupationSearch?: boolean;
    occupation?: string;
    education?: string;
    activeSkills?: SkillBase[];
    skillOptions?: SkillBase[];
    searchedSkillOptions?: SkillBase[];
    occupationOptions?: FormOption[];
    suggestedSkillOptions?: SkillBase[];
    onSkillSearchChange?: (query: string) => void;
    onOccupationSearchChange?: (query: string) => void;
    onOccupationSearchSelect?: (query: string) => void;
    className?: string;
}

const SkillsForm: FC<SkillsFormProps> = ({
    isLoading,
    showSearch,
    showOccupationSearch,
    occupation,
    education,
    activeSkills = [],
    skillOptions = [],
    searchedSkillOptions = [],
    occupationOptions = [],
    suggestedSkillOptions = [],
    onSkillSearchChange,
    onOccupationSearchChange,
    onOccupationSearchSelect,
    onSubmit,
    onCancel,
    className = '',
}): ReactElement => {
    const [skills, setSkills] = useState<SkillBase[]>([]);

    const [occupationSearchIsActive, toggleOccupationSearchIsActive] = useToggle(false);
    const [occupationToSearch, setOccupationToSearch] = useState<string>('');

    const formRef = useRef<HTMLFormElement>(null);

    useEffect((): void => {
        if (activeSkills.length > 0) setSkills(activeSkills);
    }, [activeSkills]);

    const handleOccupationChange = (value: SearchableOption): void => {
        setOccupationToSearch(value.label);

        if (onOccupationSearchChange && !value.value && value.label) {
            onOccupationSearchChange(value.label);
        }

        if (onOccupationSearchSelect && value.value) {
            onOccupationSearchSelect(value.value);
        }
    };

    const handleSuggestedSkillClick = (selectedSkill: SkillBase): void => {
        const optionExists = skills.find(option => option.id === selectedSkill.id);

        const updatedSelectedOptions = optionExists
            ? skills.filter(option => option.id !== selectedSkill.id)
            : [...skills, selectedSkill];

        const sortedSelectedOptions = sortSkillOptions(updatedSelectedOptions);

        setSkills(sortedSelectedOptions);
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();

        const formData = { skills };

        onSubmit(formData);
    };

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            className={`skills-form ${className}`}
        >
            {occupation && (
                <div className="skills-form__info-wrapper">
                    <h2 className="skills-form__info-title">
                        {trans('forms.skills.title.occupation')}
                    </h2>

                    <p className="skills-form__info-value">
                        {occupation}
                    </p>
                </div>
            )}

            {education && (
                <div className="skills-form__info-wrapper">
                    <h2 className="skills-form__info-title">
                        {trans('forms.skills.title.education')}
                    </h2>

                    <p className="skills-form__info-value">
                        {education}
                    </p>
                </div>
            )}

            <SkillsSelector
                isLoading={isLoading}
                showSearch={showSearch}
                value={skills}
                options={skillOptions}
                searchedOptions={searchedSkillOptions}
                onSearchChange={onSkillSearchChange}
                onChange={setSkills}
                className="skills-form__skills-selector"
            />

            {showOccupationSearch && (
                <IconButton
                    icon={occupationSearchIsActive ? 'chevron-up' : 'chevron-down'}
                    iconPos="right"
                    text={trans('forms.skills.button.occupationSearchToggle')}
                    onClick={toggleOccupationSearchIsActive}
                    className="skills-form__occupation-search-toggle"
                />
            )}

            {occupationSearchIsActive && (
                <div className="skills-form__occupation-search-wrapper">
                    <p className="skills-form__input-description">
                        {trans('forms.skills.disclaimer.occupationSearch')}
                    </p>

                    <SearchableValueInput
                        isSearchable
                        label={trans('forms.skills.input.occupation')}
                        value={occupationToSearch}
                        options={occupationOptions}
                        disabled={isLoading}
                        onChange={handleOccupationChange}
                        className="skills-form__occupation-search-input"
                    />

                    {suggestedSkillOptions.length > 0 && (
                        <SkillList
                            title={trans('forms.skills.title.suggestedSkills')}
                            skills={suggestedSkillOptions}
                            selectedSkills={skills}
                            onSelectOption={handleSuggestedSkillClick}
                            className="skills-form__occupation-skill-list"
                        />
                    )}
                </div>
            )}

            <FormActionButtons
                isLoading={isLoading}
                onCancelClick={onCancel}
                className="skills-form__action-buttons"
            />
        </form>
    );
};

export default SkillsForm;
